/* ============================================================================
   LegalSplash — Build 9 reskin.
   Uses Modal primitive + Button primary. Same tokens as every other screen.
   No more radial gradient glows, glass-strong, or raw hex CTAs.
   ============================================================================ */
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { Shield, X } from "lucide-react";
import { useSession } from "./SessionProvider";
import { LegalReader } from "./LegalReader";
import { Button } from "@/components/ui/NexButton";

export function LegalSplash() {
  const { legalOpen, legalAcknowledged, acknowledgeLegal, closeLegal } = useSession();
  const [readerOpen, setReaderOpen] = useState(false);
  if (!legalOpen) return null;

  const isFirstLaunch = !legalAcknowledged;

  return (
    <div
      role="dialog"
      aria-label="Legal disclaimer"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-8 bg-black/40 backdrop-blur-sm"
      style={{
        pointerEvents: "auto",
        touchAction: "manipulation",
        paddingTop: "max(2rem, var(--nx-safe-top))",
        paddingBottom: "max(2rem, var(--nx-safe-bottom))",
      }}
      onClick={isFirstLaunch ? undefined : closeLegal}
    >
      {/* E42: the disclaimer card is now a bounded flex column. The body
          (logo, copy) scrolls if it is taller than the viewport, and the CTA
          lives in a pinned footer that always carries the iOS home-indicator
          safe-area inset as bottom padding, so "I Understand" is never pushed
          off the bottom edge on short / zoomed screens. max-h is capped to the
          viewport minus the outer safe-area padding so the card itself never
          exceeds the screen. */}
      {/* While the full reader is open the card is hidden (layout kept), so
          it can never show through the reader's translucent backdrop or its
          fade-in, and assistive tech sees one dialog, not two. */}
      <div
        data-testid="legal-splash-card"
        aria-hidden={readerOpen || undefined}
        className={`w-full max-w-md max-h-full bg-[var(--surface-overlay)] border border-[var(--border-subtle)] rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] relative flex flex-col overflow-hidden${readerOpen ? " invisible" : ""}`}
        onClick={(e) => e.stopPropagation()}
        style={{ pointerEvents: "auto", touchAction: "manipulation" }}
      >
        {!isFirstLaunch && (
          <button
            data-testid="button-close-legal"
            onClick={closeLegal}
            className="absolute top-4 right-4 z-10 w-8 h-8 rounded-[var(--radius-sm)] flex items-center justify-center text-[var(--text-tertiary)] hover:bg-[var(--accent-subtle)] hover:text-[var(--text-primary)] transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-7 pt-7 pb-4" data-testid="legal-splash-body">
          {/* Logo, b13d S2: use the in-app header mark (same artwork AppShell
              renders) instead of the heavier splash tile. */}
          <div className="flex items-center justify-center mb-5">
            <Logo size={48} withWordmark tagline variant="mark" />
          </div>

          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-2 mb-1">
            <Shield className="w-3.5 h-3.5 text-[var(--status-info)]" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">
              Before you start
            </span>
          </div>

          <h2 className="text-center text-2xl font-bold tracking-tight mt-2 text-[var(--text-primary)]">
            Welcome to Nexus Dex
          </h2>
          <p className="text-center text-sm text-[var(--text-secondary)] mt-1.5">
            Your Trainer Companion Hub, by trainers, for trainers.
          </p>

          <div className="mt-6 space-y-4 text-sm leading-relaxed text-[var(--text-secondary)]">
            <p>
              Nexus Dex is{" "}
              <span className="font-semibold text-[var(--text-primary)]">
                not affiliated with, endorsed by, sponsored by, or associated with
              </span>{" "}
              Niantic, Inc., Nintendo, Game Freak, The Pokémon Company, or Scopely Explore. "Pokémon"
              and Pokémon character names are trademarks of Nintendo. All Pokémon imagery is
              the intellectual property of its respective owners and is shown here for
              informational purposes only.
            </p>
            <p>
              Nexus Dex works{" "}
              <span className="font-semibold text-[var(--text-primary)]">
                only with screenshots and recordings you choose to share with us
              </span>
              . It never connects to your Pokémon GO account, never reads game data directly,
              and never automates gameplay. It is built to respect Scopely Explore's Terms of Service for Pokémon GO; how those terms are enforced is the publisher's decision, not ours.
            </p>
          </div>
        </div>

        {/* Pinned CTA footer. legal-splash-footer carries the safe-area inset
            so the button always clears the iOS home indicator with padding
            below it. */}
        <div
          className="legal-splash-footer shrink-0 px-7 pt-4 border-t border-[var(--border-subtle)] bg-[var(--surface-overlay)]"
          data-testid="legal-splash-footer"
        >
          <Button
            type="button"
            variant="primary"
            size="lg"
            data-testid="button-acknowledge-legal"
            onClick={acknowledgeLegal}
            className="w-full"
            style={{
              /* Keep native tap routing for iOS WKWebView reliability */
              pointerEvents: "auto",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "var(--accent-fill-glow-subtle)",
            } as React.CSSProperties}
          >
            I Understand
          </Button>

          <button
            type="button"
            onClick={() => setReaderOpen(true)}
            data-testid="button-open-legal-reader"
            className="mt-3 w-full text-center text-xs font-semibold text-[var(--accent-fill)] hover:opacity-80 rounded-[var(--radius-pill)] py-2 transition-opacity"
            style={{ pointerEvents: "auto", touchAction: "manipulation" }}
          >
            Read full legal pack, Terms &amp; Privacy
          </button>

          <p className="mt-3 text-center text-[10px] text-[var(--text-tertiary)] tracking-wide">
            v11, NSW Australia law, globally enforceable
          </p>
        </div>
      </div>
      <LegalReader open={readerOpen} onClose={() => setReaderOpen(false)} />
    </div>
  );
}
